import type { TFunction } from 'i18next';
import type { Control, UseFormSetValue } from 'react-hook-form';

import type { ReceiveConvoyLocationDto } from '@/entities/receive';
import {
  ConfirmArrivalLineDescription,
  ConfirmArrivalQuantityFigure,
  getExpectedTotal,
  type ConfirmArrivalQuantityField,
  type ConfirmArrivalUnitMode
} from '@/features/receive-confirm-arrival';
import type { SelectOption } from '@/shared/ui/filters';
import type { DataTableColumn } from '@/shared/ui/data-table';

import type { ReceiveConvoyTableRow } from '../lib/table-rows';
import type { ReceiveConvoyFormValues } from '../model/types';
import {
  ReceiveConvoyLocationCell,
  ReceiveConvoyQuantityCell,
  ReceiveConvoyRemainingCell
} from './receive-convoy-line-cells';

interface ConvoyColumnArgs {
  control: Control<ReceiveConvoyFormValues>;
  setValue: UseFormSetValue<ReceiveConvoyFormValues>;
  unitMode: ConfirmArrivalUnitMode;
  showLostColumn: boolean;
  isSecondaryOpen: (lineId: string, field: ConfirmArrivalQuantityField) => boolean;
  onToggleSecondary: (lineId: string, field: ConfirmArrivalQuantityField) => void;
  locationOptions: SelectOption[];
  locationsById: Map<string, ReceiveConvoyLocationDto>;
}

/**
 * Columns of the convoy table. Only `line` rows reach these cells — the truck
 * header and documents rows are full-width group rows. Every form-bound cell is
 * a component subscribing to its own line, so the table itself never re-renders
 * on a keystroke.
 */
export const getReceiveConvoyColumns = (
  t: TFunction,
  {
    control,
    setValue,
    unitMode,
    showLostColumn,
    isSecondaryOpen,
    onToggleSecondary,
    locationOptions,
    locationsById
  }: ConvoyColumnArgs
): DataTableColumn<ReceiveConvoyTableRow>[] => {
  const quantityColumn = (
    field: ConfirmArrivalQuantityField,
    label: string
  ): DataTableColumn<ReceiveConvoyTableRow> => ({
    id: field,
    header: label,
    width: 'w-42.5',
    cell: (row) => {
      if (row.kind !== 'line') return null;
      // A truck that may not record a loss keeps an empty Lost cell.
      if (field === 'lostQty' && !row.shipment.lostQtyAllowed) return null;
      return (
        <ReceiveConvoyQuantityCell
          control={control}
          setValue={setValue}
          line={row.line}
          shipmentIndex={row.shipmentIndex}
          lineIndex={row.lineIndex}
          field={field}
          label={label}
          unitMode={unitMode}
          secondaryOpen={isSecondaryOpen(row.line.lineId, field)}
          onToggleSecondary={() => onToggleSecondary(row.line.lineId, field)}
        />
      );
    }
  });

  return [
    {
      id: 'line',
      header: t('receive.requestline.label', 'Consignment Line'),
      width: 'w-60',
      cell: (row) => (row.kind === 'line' ? <ConfirmArrivalLineDescription line={row.line} /> : null)
    },
    {
      id: 'expected',
      header: t('receive.expectedquantity.label', 'Quantity Expected'),
      width: 'w-32.5',
      cell: (row) =>
        row.kind === 'line' ? (
          <ConfirmArrivalQuantityFigure
            line={row.line}
            unitMode={unitMode}
            quantity={getExpectedTotal(row.line)}
            kind="total"
          />
        ) : null
    },
    quantityColumn('goodQty', t('receive.goodquantity.label', 'Received in Good Condition')),
    quantityColumn('damagedQty', t('receive.badquantity.label', 'Received in Damaged Condition')),
    ...(showLostColumn ? [quantityColumn('lostQty', t('receive.lostintransit.label', 'Lost in Transit'))] : []),
    {
      id: 'location',
      header: t('receive.receivelocation.label', 'Receive Location'),
      width: 'w-56',
      cell: (row) =>
        row.kind === 'line' ? (
          <ReceiveConvoyLocationCell
            control={control}
            setValue={setValue}
            shipmentIndex={row.shipmentIndex}
            lineIndex={row.lineIndex}
            locationOptions={locationOptions}
            locationsById={locationsById}
          />
        ) : null
    },
    {
      id: 'remaining',
      header: t('receive.remainingquantity.label', 'Remaining Quantity'),
      width: 'w-32.5',
      cell: (row) =>
        row.kind === 'line' ? (
          <ReceiveConvoyRemainingCell
            control={control}
            line={row.line}
            shipmentIndex={row.shipmentIndex}
            lineIndex={row.lineIndex}
            unitMode={unitMode}
          />
        ) : null
    }
  ];
};
